/**
 * Formas con puntos editables del layout (paso 1.4): vías y muros (líneas) y pisos (polígonos
 * cerrados). Medidas de cada una para Propiedades y las cotas.
 */

import type { Layout, Punto } from '../dominio/espacio';

export type TipoForma = 'via' | 'piso' | 'muro';

export interface FormaConPuntos {
  tipo: TipoForma;
  id: string;
  puntos: readonly Punto[];
  /** Polígono cerrado (piso): el último punto se une con el primero. */
  cerrada: boolean;
}

/** La vía, el piso o el muro con ese identificador. */
export function formaPorId(
  layout: Pick<Layout, 'vias' | 'pisos' | 'muros'>,
  id: string,
): FormaConPuntos | null {
  const v = layout.vias.find((x) => x.id === id);
  if (v) return { tipo: 'via', id, puntos: v.puntos, cerrada: false };
  const p = layout.pisos.find((x) => x.id === id);
  if (p) return { tipo: 'piso', id, puntos: p.puntos, cerrada: true };
  const m = layout.muros.find((x) => x.id === id);
  if (m) return { tipo: 'muro', id, puntos: m.puntos, cerrada: false };
  return null;
}

/** Área de un polígono (fórmula del área de Gauss), en m². */
export function areaDePoligono(puntos: readonly Punto[]): number {
  let s = 0;
  for (let i = 0; i < puntos.length; i++) {
    const a = puntos[i]!;
    const b = puntos[(i + 1) % puntos.length]!;
    s += a.x * b.z - b.x * a.z;
  }
  return Math.abs(s) / 2;
}

/** Largo de una línea de puntos; cerrada, suma el tramo del último al primero. */
export function largoDe(puntos: readonly Punto[], cerrada = false): number {
  let s = 0;
  const n = puntos.length;
  for (let i = 1; i < n + (cerrada ? 1 : 0); i++) {
    const a = puntos[i - 1]!;
    const b = puntos[i % n]!;
    s += Math.hypot(b.x - a.x, b.z - a.z);
  }
  return s;
}

/** Rectángulo de `ancho × fondo` centrado en `c`, con los lados en x y z (sentido horario). */
export function rectangulo(c: Punto, ancho: number, fondo: number): Punto[] {
  const r = (v: number) => Math.round(v * 1000) / 1000;
  const x0 = r(c.x - ancho / 2);
  const x1 = r(c.x + ancho / 2);
  const z0 = r(c.z - fondo / 2);
  const z1 = r(c.z + fondo / 2);
  return [
    { x: x0, z: z0 },
    { x: x1, z: z0 },
    { x: x1, z: z1 },
    { x: x0, z: z1 },
  ];
}

/**
 * Punto siguiente de una línea con el ángulo ajustado a múltiplos de 45° desde el anterior (como el
 * v6 con «ortogonal»): se conserva el largo, redondeado al `paso`.
 */
export function a45Grados(previo: Punto, q: Punto, paso: number): Punto {
  const dx = q.x - previo.x;
  const dz = q.z - previo.z;
  const largo = Math.round(Math.hypot(dx, dz) / paso) * paso;
  const a = Math.round(Math.atan2(dz, dx) / (Math.PI / 4)) * (Math.PI / 4);
  const r = (v: number) => Math.round(v * 100) / 100 || 0;
  return { x: r(previo.x + Math.cos(a) * largo), z: r(previo.z + Math.sin(a) * largo) };
}

/**
 * Puntos de una forma con un punto movido o insertado (vista previa del arrastre de una manija,
 * igual que el comando al soltar). `indice = puntos.length` inserta al final (polígono cerrado).
 */
export function puntosEditados(
  puntos: readonly Punto[],
  indice: number,
  insertar: boolean,
  punto: Punto,
): Punto[] {
  const r = puntos.map((p) => ({ ...p }));
  if (insertar) r.splice(indice, 0, { ...punto });
  else if (r[indice]) r[indice] = { ...punto };
  return r;
}

/** Rectángulo de un tramo de `a` a `b` con `ancho` (calzada de una vía, huella de un muro). */
export function rectanguloDeSegmento(a: Punto, b: Punto, ancho: number): Punto[] {
  const largo = Math.hypot(b.x - a.x, b.z - a.z) || 1;
  const nx = (-(b.z - a.z) / largo) * (ancho / 2);
  const nz = ((b.x - a.x) / largo) * (ancho / 2);
  return [
    { x: a.x + nx, z: a.z + nz },
    { x: b.x + nx, z: b.z + nz },
    { x: b.x - nx, z: b.z - nz },
    { x: a.x - nx, z: a.z - nz },
  ];
}

/** Huella de un muro: un rectángulo por tramo, alargado medio espesor en cada punta. */
export function huellasDeMuro(m: { puntos: readonly Punto[]; espesor: number }): Punto[][] {
  const r: Punto[][] = [];
  for (let i = 1; i < m.puntos.length; i++) {
    const a = m.puntos[i - 1]!;
    const b = m.puntos[i]!;
    const largo = Math.hypot(b.x - a.x, b.z - a.z);
    if (largo < 0.01) continue;
    const ux = (b.x - a.x) / largo;
    const uz = (b.z - a.z) / largo;
    const e = m.espesor / 2;
    r.push(
      rectanguloDeSegmento(
        { x: a.x - ux * e, z: a.z - uz * e },
        { x: b.x + ux * e, z: b.z + uz * e },
        m.espesor,
      ),
    );
  }
  return r;
}
